import { useLingui } from '@lingui/react/macro'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useGraphNodeSelection } from 'features/knowledgeDocumentViewer/graph/GraphNodeSelectionContext'
import type { ReactNode } from 'react'
import styled from 'styled-components'

import { DocumentMetadataSection } from './DocumentMetadataSection'
import { EvalMetadataSection } from './EvalMetadataSection'
import { GraphNodePanel } from './GraphNodePanel'
import { MobileDetailSections } from './MobileDetailSections'
import { OcrTable } from './OcrTable'
import { SubjectDocumentsPanel } from './SubjectDocumentsPanel'

const Sidebar = styled.div<{
  $placement: 'side' | 'bottom'
  $scrollWithContent: boolean
}>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--card);
  --edge-fade-from: var(--card);
  border-left: ${({ $placement }) =>
    $placement === 'side' ? '0.0625rem solid var(--border)' : 'none'};
  height: ${({ $scrollWithContent }) => ($scrollWithContent ? 'auto' : '100%')};
  max-height: 100%;
  width: 100%;
  min-width: 0;
  min-height: 0;
`

const SidebarContent = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

export type KnowledgeDocumentSidebarProps = {
  crossReferencePanel?: ReactNode
  lotoPanel?: ReactNode
  handleZoomToBox: (id: string) => void
  placement?: 'side' | 'bottom'
  selectedSubjectUid?: string | null
  clearSelectedSubject?: () => void
}

export function KnowledgeDocumentSidebar({
  handleZoomToBox,
  placement = 'side',
  selectedSubjectUid,
  clearSelectedSubject,
  crossReferencePanel,
  lotoPanel,
}: KnowledgeDocumentSidebarProps) {
  const { t } = useLingui()
  const nodeSelection = useGraphNodeSelection()
  const { isEvalSessionLocked, currentDocumentId } =
    useKnowledgeDocumentUXContext()
  const panel = crossReferencePanel ?? lotoPanel
  const showLotoPanel = !crossReferencePanel && Boolean(lotoPanel)
  const scrollWithContent =
    placement === 'bottom' && !isEvalSessionLocked && !showLotoPanel
  const showSubjectPanel = !isEvalSessionLocked && Boolean(selectedSubjectUid)
  const subject =
    showSubjectPanel && selectedSubjectUid ? (
      <SubjectDocumentsPanel
        subjectUid={selectedSubjectUid}
        scrollWithParent={scrollWithContent}
        onClose={() => clearSelectedSubject?.()}
      />
    ) : null
  const primaryPanel =
    panel ??
    (nodeSelection.selection ? (
      <GraphNodePanel
        selection={nodeSelection.selection}
        onClose={nodeSelection.clear}
        scrollWithParent={scrollWithContent}
      />
    ) : (
      subject
    ))
  const details = !showLotoPanel && (
    <DocumentMetadataSection
      besideSubject={
        showSubjectPanel || Boolean(nodeSelection.selection) || Boolean(panel)
      }
      scrollWithParent={scrollWithContent}
    />
  )

  return (
    <Sidebar $placement={placement} $scrollWithContent={scrollWithContent}>
      <SidebarContent>
        {scrollWithContent ? (
          <MobileDetailSections
            key={currentDocumentId}
            subject={panel || nodeSelection.selection ? null : subject}
            subjectUid={selectedSubjectUid}
            subjectLabel={t`Subject`}
            details={details}
            detailsLabel={t`Document details`}>
            {panel || nodeSelection.selection ? primaryPanel : null}
            <OcrTable handleZoomToBox={handleZoomToBox} />
          </MobileDetailSections>
        ) : (
          <>
            {primaryPanel}
            {isEvalSessionLocked ? null : (
              <OcrTable handleZoomToBox={handleZoomToBox} />
            )}
            {isEvalSessionLocked ? <EvalMetadataSection /> : details}
          </>
        )}
      </SidebarContent>
    </Sidebar>
  )
}
